// Logic for the team dashboard - see doc/team-dashboard-concept.org.
//
// Everything here is pure and the clock is always a parameter (`today`, a Date,
// or a 'YYYY-MM-DD' day key handed in) - exactly like lib/contract.js and
// lib/focus.js - so all of it unit-tests without rendering and without freezing
// timers.
//
// Unlike Contract/Week/Focus this is not daily-cadence data: a change-track
// line, a quick win or a pulse reading lands on whatever day it lands on, and
// the change track itself is one evolving object rather than a per-day record.
// So this module shapes exactly one state object - `{ track, quickWins,
// reviews, pulses }` - which lives in one persistent file (lib/team_org.js).
//
// What is deliberately absent (concept §3, §6): any velocity or throughput
// number, any score the lead guessed on the team's behalf, and any statistic
// computed over the pulse readings beyond sorting them. The 2024 DORA data has
// individual AI-assisted productivity rising while team-level throughput and
// delivery stability fall, so a velocity number here would surface exactly the
// half of that trade-off that predicts harm.

// The same local-date key the contract dashboard files its days under - shared
// rather than re-derived, since getting it wrong (toISOString(), which formats
// in UTC) is the one trap both modules have to avoid.
import { dayKey } from './contract';

/**
 * The eight change-track lines (concept §4.1): Kotter's ingredients, each
 * markable on its own, none gated on another - Appelbaum et al. (2012) found no
 * empirical basis for running the steps in sequence, or as a package at all.
 *
 * `key` is the file format (lib/team_org.js writes it as an Org tag), `label`
 * is copy and can be reworded freely without losing anybody's state.
 */
export const CHANGE_TRACK = [
  {
    key: 'reason',
    label: 'The reason this matters, said out loud',
    // Kotter's step 1 is "establishing a sense of urgency". Renamed on purpose
    // (§4.1): manufactured urgency is reported to backfire with technical teams,
    // and the "70% of change efforts fail" figure normally used to manufacture
    // it was traced by Hughes (2011) to no valid source in any of its five most
    // cited origins. What is left, honestly, is having said the real reason once.
    caution: 'Say the real reason once, plainly. Do not manufacture urgency (§2.2).',
  },
  { key: 'coalition', label: 'A guiding group, not the lead alone' },
  { key: 'vision', label: 'A picture of the new way of working' },
  { key: 'communication', label: 'Communicated plainly, and more than once' },
  { key: 'empowerment', label: 'What stopped people acting on it, removed' },
  {
    key: 'wins',
    label: 'Short-term wins planned for, not hoped for',
    // The one Kotter step with peer-reviewed grounding independent of Kotter
    // himself (Weick 1984) - which is why §4.2 gives it its own log.
  },
  { key: 'consolidation', label: 'What worked consolidated, then more changed' },
  { key: 'anchoring', label: 'Anchored in how the team normally works' },
];

export const TRACK_STATES = ['notStarted', 'active', 'anchored'];

export const TRACK_STATE_LABELS = {
  notStarted: 'not started',
  active: 'active',
  anchored: 'anchored',
};

/**
 * One tap moves a line on; from anchored it wraps back - nothing is one-way.
 *
 * An unrecognised state counts as /not started/ and advances from there, rather
 * than swallowing the tap.
 */
export const nextTrackState = (state) => {
  const index = TRACK_STATES.indexOf(state);
  return TRACK_STATES[((index < 0 ? 0 : index) + 1) % TRACK_STATES.length];
};

// Was the call made by the group, or by one person accepting an AI suggestion?
// Named plainly and never inferred (concept §4.3) - a solo-accepted suggestion
// on a real decision is precisely the overconfidence Perry et al. (2023)
// measured, so the two cases must stay distinguishable in the record.
export const REVIEW_DECISIONS = ['group', 'solo'];

// One persistent file, not a daily note (concept §5) - overridable through the
// `teamAdoptionPath` setting.
export const DEFAULT_TEAM_ADOPTION_PATH = '/roam/team.org';

export const DEFAULT_PULSE_INTERVAL_DAYS = 21;

// Readings are entered on Edmondson's usual 1-5 agreement scale. A team that
// asks its question on some other scale is not rejected: `pulseScaleMax` widens
// the axis instead of dropping the reading (see below).
export const PULSE_SCALE_MAX = 5;

const DAY_KEY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const isDayKey = (value) => typeof value === 'string' && DAY_KEY_REGEX.test(value);

const MILLIS_PER_DAY = 24 * 60 * 60 * 1000;

/** A day key back to a local midnight Date; null for anything that is not one. */
export const dateForDayKey = (key) => {
  if (!isDayKey(key)) return null;
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/** Whole days from `fromKey` to `toKey`, or null if either is not a day key. */
export const daysBetween = (fromKey, toKey) => {
  const from = dateForDayKey(fromKey);
  const to = dateForDayKey(toKey);
  if (!from || !to) return null;

  // Rounded, not floored: a DST boundary inside the span shifts the difference
  // by an hour, and that must not turn 21 days into 20.
  return Math.round((to.getTime() - from.getTime()) / MILLIS_PER_DAY);
};

const trimmed = (value) => (typeof value === 'string' ? value.trim() : '');

// Every text field here is stored as exactly one line of an Org file
// (lib/team_org.js), so a pasted paragraph is folded into one line on the way in
// rather than breaking the file's structure on the way out.
const oneLine = (value) => trimmed(value).replace(/\s+/g, ' ');

const byDate = (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0);

/**
 * The eight track lines, always all eight, in the order of CHANGE_TRACK -
 * whatever was read out of the file (or nothing at all).
 *
 * An unknown key is dropped and a missing one comes back /not started/: the file
 * is hand-editable, and one mangled line must not take the page down.
 */
export const normalizeTrack = (track) => {
  const byKey = {};
  (Array.isArray(track) ? track : []).forEach((entry) => {
    if (entry && entry.key) byKey[entry.key] = entry;
  });

  return CHANGE_TRACK.map((ingredient) => {
    const stored = byKey[ingredient.key] || {};

    return {
      key: ingredient.key,
      label: ingredient.label,
      caution: ingredient.caution || null,
      state: TRACK_STATES.includes(stored.state) ? stored.state : 'notStarted',
      note: oneLine(stored.note),
    };
  });
};

/** Dated one-liners, oldest first (concept §4.2). Undated or empty ones drop. */
export const normalizeQuickWins = (quickWins) =>
  (Array.isArray(quickWins) ? quickWins : [])
    .filter((win) => win && isDayKey(win.date) && !!oneLine(win.text))
    .map((win) => ({ date: win.date, text: oneLine(win.text) }))
    .sort(byDate);

/**
 * Review-gate entries, oldest first (concept §4.3).
 *
 * `verified` and `decision` may legitimately be missing - an entry logged
 * without either is a shipped change nobody wrote a verification line for, and
 * that is exactly what the tally is there to make visible. Only `change`, the
 * name of the thing that shipped, is required for the entry to mean anything.
 */
export const normalizeReviews = (reviews) =>
  (Array.isArray(reviews) ? reviews : [])
    .filter((review) => review && isDayKey(review.date) && !!oneLine(review.change))
    .map((review) => ({
      date: review.date,
      change: oneLine(review.change),
      verified: oneLine(review.verified),
      decision: REVIEW_DECISIONS.includes(review.decision) ? review.decision : null,
    }))
    .sort(byDate);

/**
 * Pulse readings, oldest first (concept §4.5), one per day - a second reading
 * for a day it already has replaces it rather than doubling the day up.
 *
 * A reading outside the 1-5 scale is kept, not dropped: some teams ask their
 * question on 1-10, and silently discarding a real number the lead did collect
 * would be worse than widening the axis for it.
 */
export const normalizePulses = (pulses) => {
  const byDay = {};

  (Array.isArray(pulses) ? pulses : []).forEach((pulse) => {
    if (!pulse || !isDayKey(pulse.date)) return;

    const score = Number(pulse.score);
    if (!Number.isFinite(score) || score <= 0) return;

    byDay[pulse.date] = { date: pulse.date, score };
  });

  return Object.keys(byDay)
    .map((key) => byDay[key])
    .sort(byDate);
};

/** Whatever was read out of the team file into the one shape this module works on. */
export const normalizeTeamState = (state) => ({
  track: normalizeTrack(state && state.track),
  quickWins: normalizeQuickWins(state && state.quickWins),
  reviews: normalizeReviews(state && state.reviews),
  pulses: normalizePulses(state && state.pulses),
});

/** A state with nothing in it yet: eight /not started/ lines and three empty logs. */
export const emptyTeamState = () => normalizeTeamState(null);

export const setTrackState = (state, key, nextState) => {
  const normalized = normalizeTeamState(state);

  return {
    ...normalized,
    track: normalized.track.map((entry) =>
      entry.key === key
        ? { ...entry, state: TRACK_STATES.includes(nextState) ? nextState : entry.state }
        : entry
    ),
  };
};

/** One free-text line per track line (§4.1): what is actually being done for it. */
export const setTrackNote = (state, key, note) => {
  const normalized = normalizeTeamState(state);

  return {
    ...normalized,
    track: normalized.track.map((entry) =>
      entry.key === key ? { ...entry, note: oneLine(note) } : entry
    ),
  };
};

/**
 * Append a quick win. Returns the state unchanged if there is no text to append -
 * the log is append-only (§4.2), so an empty entry would be permanent noise.
 */
export const addQuickWin = (state, { text, today }) => {
  const normalized = normalizeTeamState(state);
  const value = oneLine(text);
  if (!value) return normalized;

  return {
    ...normalized,
    quickWins: normalizeQuickWins([...normalized.quickWins, { date: dayKey(today), text: value }]),
  };
};

/** Append one shipped change's review-gate entry (§4.3). Needs at least a name. */
export const addReview = (state, { change, verified, decision, today }) => {
  const normalized = normalizeTeamState(state);
  const name = oneLine(change);
  if (!name) return normalized;

  return {
    ...normalized,
    reviews: normalizeReviews([
      ...normalized.reviews,
      { date: dayKey(today), change: name, verified, decision },
    ]),
  };
};

/** Log one pulse reading (§4.5). A day already logged is replaced, not doubled. */
export const addPulse = (state, { score, today }) => {
  const normalized = normalizeTeamState(state);
  const value = Number(score);
  if (!Number.isFinite(value) || value <= 0) return normalized;

  return {
    ...normalized,
    pulses: normalizePulses([...normalized.pulses, { date: dayKey(today), score: value }]),
  };
};

/** Plain counts over the eight lines - a tally, never a completion score (§3). */
export const trackSummary = (track) => {
  const entries = normalizeTrack(track);

  return {
    total: entries.length,
    active: entries.filter((entry) => entry.state === 'active').length,
    anchored: entries.filter((entry) => entry.state === 'anchored').length,
    withNote: entries.filter((entry) => !!entry.note).length,
  };
};

/**
 * The review-gate tally (concept §5): two plain ratios, nothing modelled.
 *
 * `withNote / shipped` is how often a shipped change actually got a line saying
 * what was checked - the thing Tang et al. (2024) found gets skipped when the
 * code is not visibly AI-labeled. `groupDecided / shipped` is how often the call
 * was the group's rather than one person's. `unnamed` is neither, and is shown
 * rather than folded into `solo`: an entry whose decision was never named is not
 * evidence that it was solo.
 */
export const reviewTally = (reviews) => {
  const entries = normalizeReviews(reviews);
  const groupDecided = entries.filter((entry) => entry.decision === 'group').length;
  const soloDecided = entries.filter((entry) => entry.decision === 'solo').length;

  return {
    shipped: entries.length,
    withNote: entries.filter((entry) => !!entry.verified).length,
    groupDecided,
    soloDecided,
    unnamed: entries.length - groupDecided - soloDecided,
  };
};

/** The readings, sorted, and nothing else done to them (concept §4.5, §5). */
export const pulseTrend = (pulses) => normalizePulses(pulses);

/** The axis bound for the trend line: the 1-5 scale, widened by any reading above it. */
export const pulseScaleMax = (pulses) =>
  normalizePulses(pulses).reduce((max, pulse) => Math.max(max, pulse.score), PULSE_SCALE_MAX);

/**
 * Whether it is time to ask the team again (concept §4.5).
 *
 * With no reading yet it is due - that is the state a new file starts in, and a
 * reminder is the whole point of the section. `daysSince` is null in that case
 * rather than a made-up zero.
 */
export const pulseDue = (pulses, today, intervalDays = DEFAULT_PULSE_INTERVAL_DAYS) => {
  const entries = normalizePulses(pulses);
  const latest = entries.length ? entries[entries.length - 1] : null;

  const interval = Number(intervalDays);
  const days = Number.isFinite(interval) && interval > 0 ? interval : DEFAULT_PULSE_INTERVAL_DAYS;

  if (!latest) return { due: true, daysSince: null, lastDate: null, intervalDays: days };

  const daysSince = daysBetween(latest.date, dayKey(today));

  return {
    due: daysSince === null || daysSince >= days,
    daysSince,
    lastDate: latest.date,
    intervalDays: days,
  };
};

/**
 * The idea-genesis card (concept §4.4): a reminder of the process, not a form.
 *
 * Static on purpose - §3 rejects tracking compliance with each of these, since
 * nothing suggests the logging would be worth what it would cost. The `strength`
 * line is part of the content, not decoration: a reader should be able to see
 * which of these rests on a meta-analysis and which on one contested study.
 */
export const IDEA_GENESIS = [
  {
    text: 'Draft or prompt alone first. The group discusses and chooses second.',
    why:
      'Interacting groups produce fewer and worse ideas than the same people pooled - production blocking. A group jointly prompting one AI session reproduces it: one keyboard, everyone else waiting.',
    strength: 'strong (Diehl & Stroebe 1987; Mullen et al. 1991 meta; Girotra et al. 2010)',
  },
  {
    text: 'Leave a real gap - hours, ideally a day - before deciding anything non-trivial.',
    why:
      'Incubation reliably improves what comes back, more so for divergent tasks, and more when the break is not filled with another demanding task.',
    strength: 'strong (Sio & Ormerod 2009, meta of 117 studies)',
  },
  {
    text: 'Prefer several rough parallel attempts over one polished serial one.',
    why:
      'Parallel prototypes beat serial iteration on every measure tried, including the makers’ own self-efficacy.',
    strength: 'medium (Dow et al. 2010, N=33, unreplicated)',
  },
  {
    text: 'Disagree openly rather than deferring judgment - in review as in ideation.',
    why:
      'Instructing groups to debate and criticize outproduced the classic "defer all judgment" rule. Held loosely: essentially one experiment plus an unpublished working paper.',
    strength: 'weak (Nemeth et al. 2004; Feinberg & Nemeth 2008 unpublished)',
  },
  {
    text: 'Keep review about the work, never about the person.',
    why:
      'Feedback raises performance on average but lowers it in roughly a third of cases - direction depends on whether it lands on the task or on the person.',
    strength: 'strong, double-edged (Kluger & DeNisi 1996, 607 effect sizes)',
  },
];
